import type React from 'react';
import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { ROUTE_CONFIG, ROUTES } from '@/routes/config';
import { useAuthStore } from '@/stores/slices/useAuthStore';
import { usePanelStore } from '@/stores/slices/usePanelStore';

/**
 * Syncs the sidebar panel with the current URL path.
 * Example: visiting /vessels directly will open the Vessels panel.
 */
const RoutePanelSync: React.FC = () => {
  const location = useLocation();
  const setPanel = usePanelStore((state) => state.setPanel);
  const panel = usePanelStore((state) => state.panel);
  const getToken = useAuthStore((state) => state.getToken);

  useEffect(() => {
    const path = location.pathname;

    // Helper to set panel only if it's not already active (prevents loops)
    const syncPanel = (name: string) => {
      if (panel.name !== name) {
        setPanel({ name, content: null });
      }
    };

    // Check for deep links FIRST (before exact matches) to handle detail views
    // 3. Handle Deep Links (e.g. /vessels/:id, /rules-alerts/:id)
    const vesselMatch = matchPath(ROUTES.VESSEL_DETAILS, path);
    if (vesselMatch) {
      const id = vesselMatch.params.id;
      if (id) {
        // Get current MMSI for comparison
        const currentMmsi =
          panel.content && typeof panel.content === 'object' && 'mmsi' in panel.content
            ? String(panel.content.mmsi)
            : null;

        // Skip SpaceEyes API - go directly to CDC-powered vessel detail page
        // VesselDetailPage will fetch vessel data from CDC API
        if (panel.name !== 'Vessel' || currentMmsi !== id) {
          setPanel({ name: 'Vessel', content: { mmsi: id } });
        }
      }
      return;
    }

    const ruleMatch = matchPath(ROUTES.RULE_DETAILS, path);
    if (ruleMatch) {
      const id = ruleMatch.params.id;
      if (id) {
        const loadRule = async () => {
          try {
            // Dynamically import to avoid circular dependencies if any, or just use the service
            const { fetchRules } = await import('@/services/rulesService');
            const token = await getToken();
            if (!token) return;

            const rules = await fetchRules(token);
            const target = rules.find((r) => r.id === id);

            if (target) {
              const currentId =
                panel.content && typeof panel.content === 'object' && 'id' in panel.content ? panel.content.id : null;
              if (panel.name !== 'Rule' || currentId !== target.id) {
                setPanel({ name: 'Rule', content: target });
              }
            }
          } catch (error) {
            console.error('Error loading rule for deep link:', error);
          }
        };
        loadRule();
      }
      return;
    }

    // 1. Check for exact matches in configuration
    // When viewing a detail panel (e.g., Vessel, Rule), only block sync if navigating
    // to the parent section (e.g., /vessels while viewing a vessel). Allow navigation
    // to other sections (e.g., /network, /drones) so sidebar clicks always work.
    const detailPanelParentRoutes: Record<string, string> = {
      Vessel: ROUTES.VESSELS,
      Rule: ROUTES.RULES_ALERTS,
      Fleet: ROUTES.FLEET_MANAGER,
    };
    const configEntry = ROUTE_CONFIG[path];
    if (configEntry) {
      const parentRoute = detailPanelParentRoutes[panel.name];
      if (!parentRoute || parentRoute !== path) {
        syncPanel(configEntry.panel);
      }
      return;
    }

    // 2. Handle Home route explicitly - ensure panel is empty
    if (path === ROUTES.HOME) {
      if (panel.name !== 'Empty') {
        setPanel({ name: 'Empty', content: null });
      }
      return;
    }
  }, [location.pathname, setPanel, panel.name, panel.content, getToken]);

  return null;
};

export default RoutePanelSync;
